import React, { useEffect, useState } from 'react'
import * as yup from 'yup'
import { Formik } from 'formik'
import { Header2, Header3, Subtitle1, Subtitle3 } from 'pharmacy/src/typography'
import { useDispatch, useSelector } from 'react-redux'
import classNames from 'classnames'

import { TextInput } from 'pharmacy/src/input/textInput'
import { Button } from 'pharmacy/src/input/button'
import { Icon } from 'pharmacy/src/display/icon'

import { StarLoader } from 'pharmacy/src/misc/loaders/starLoader'

import {
  blockInvites,
  fetchUnsubscribeEmail,
  FETCH_USER_WITH_PERMISSIONS_PUBLIC,
} from 'mednet-cns/src/reducers/user'
import { getRequest } from 'mednet-cns/src/api/v1'

import { mixpanel } from 'mednet-util/src/tracking'
import { isUserPermittedTo } from 'mednet-util/src/permission'
import { authItems } from 'mednet-util/src/constants/permission'

import css from './index.scss'

const schema = yup.object().shape({
  targetEmailAddress: yup
    .string()
    .email('Invalid email')
    .max(128, 'Too long')
    .required('Required'),
})

const SUBMIT_ERROR =
  'Something went wrong. Please try again, or email contact@themednet.org and we will unsubscribe you.'

// Shown in place of the form, so it does not invite a retry the page cannot make.
const LOAD_ERROR =
  'We could not open this unsubscribe request. Please email contact@themednet.org and we will unsubscribe you.'

export const SubmitInvitesUnsubscription = (props) => {
  const params = new URLSearchParams(props.location.search)
  const userEmail = params.get('user_email')
  const userId = params.get('user_id')
  const token = params.get('token')
  const invitationId = params.get('invitation_id')

  const dispatch = useDispatch()

  const [isSubmitted, setSubmitted] = useState(false)
  const [submittedEmail, setSubmittedEmail] = useState()
  const [submitError, setSubmitError] = useState(null)

  // Read from the store rather than fetched here: the public user request that
  // populates it runs on every page, and the logged-in variant would fail for
  // the visitors this page mostly serves. Until it resolves the viewer looks
  // like a guest, so nothing that depends on it can be drawn yet.
  const permissions = useSelector((state) => state.user?.permissions)
  const isUserLoaded = useSelector(
    (state) =>
      getRequest(state, FETCH_USER_WITH_PERMISSIONS_PUBLIC).isLoaded === true
  )
  const isAdmin =
    Boolean(permissions) && isUserPermittedTo(permissions, [authItems.admin])

  // Newer links identify the recipient by id so their address never appears in
  // the URL, and the address has to be fetched before the form can show it.
  const shouldFetchPrefillEmail = !userEmail && Boolean(token)

  const [contactedEmailAddress, setContactedEmailAddress] = useState(userEmail)
  const [isLoadingEmail, setLoadingEmail] = useState(shouldFetchPrefillEmail)
  const [loadEmailError, setLoadEmailError] = useState(null)

  // A link carrying neither an address nor a token names nobody, and a lookup
  // that failed leaves us in the same place — no address to unsubscribe, and a
  // submit that would be rejected for the same reason. An admin is the
  // exception either way, since their submit skips the link check entirely.
  const isLinkUnusable =
    !isAdmin && ((!userEmail && !token) || Boolean(loadEmailError))

  const initialValues = {
    contactedEmailAddress,
    targetEmailAddress: contactedEmailAddress,
    userId,
    invitationId,
    token,
  }

  const handleSubmit = (values, { setSubmitting }) => {
    setSubmitting(true)
    setSubmitError(null)
    setSubmittedEmail(values.targetEmailAddress)
    dispatch(
      blockInvites(
        values,
        () => {
          setSubmitting(false)
          setSubmitted(true)
        },
        () => {
          setSubmitting(false)
          setSubmitError(SUBMIT_ERROR)
        }
      )
    )
  }

  useEffect(() => {
    mixpanel.track('viewed_page', { page_viewed: 'stop_invites' })
  }, [])

  // Runs once: the identifiers it sends all come from the URL, so re-running on
  // a change would only ever repeat the same lookup.
  useEffect(() => {
    if (!shouldFetchPrefillEmail) {
      return
    }

    dispatch(
      fetchUnsubscribeEmail(
        { user_id: userId, invitation_id: invitationId, token },
        (response) => {
          setContactedEmailAddress(response?.email)
          setLoadingEmail(false)
        },
        () => {
          setLoadingEmail(false)
          setLoadEmailError(LOAD_ERROR)
        }
      )
    )
  }, [])

  // Whether the form or the error belongs here depends on who is viewing, and
  // an unresolved viewer reads as a guest — so drawing either one now would show
  // the wrong thing and then swap it out. A link that already carries the
  // address draws the same form for everyone, so it has nothing to wait for.
  const isWaitingForViewer = !userEmail && !isUserLoaded

  if (isWaitingForViewer || isLoadingEmail) {
    return (
      <div className={css.centeredContainer}>
        <div className={css.row}>
          <Header3>Stop receiving invitations from Mednet</Header3>
          <hr className={css.divider} />
        </div>

        <StarLoader />
      </div>
    )
  }

  if (isSubmitted) {
    return (
      <div className={css.centeredContainer}>
        <Header2>
          <Icon icon="check-circle" className={css.checkIcon} />
          Mednet will not send any more invitations to {submittedEmail}
        </Header2>
      </div>
    )
  }

  // Nothing the form could do here, so the message stands on its own rather
  // than inviting a submit that cannot succeed.
  if (isLinkUnusable) {
    return (
      <div className={css.centeredContainer}>
        <div className={css.row}>
          <Header3>Stop receiving invitations from Mednet</Header3>
          <hr className={css.divider} />
        </div>

        <Subtitle3 className={css.errorMessage}>
          {loadEmailError || LOAD_ERROR}
        </Subtitle3>
      </div>
    )
  }

  return (
    <div className={css.centeredContainer}>
      <div className={css.row}>
        <Header3>Stop receiving invitations from Mednet</Header3>
        <hr className={css.divider} />
      </div>

      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={schema}
        validateOnBlur={false}
        validateOnChange={false}
        onSubmit={(values, formikHelpers) => {
          handleSubmit(values, formikHelpers)
        }}
      >
        {(formikProps) => {
          return (
            <>
              <div className={css.row}>
                <Subtitle1>Email</Subtitle1>
                <TextInput
                  value={formikProps.values.targetEmailAddress}
                  onChange={(value) =>
                    formikProps.setFieldValue('targetEmailAddress', value)
                  }
                  className={classNames(css.emailField, {
                    [css.emailFieldWithError]: Boolean(
                      formikProps.errors['targetEmailAddress']
                    ),
                  })}
                  showClear={false}
                />

                {formikProps.errors['targetEmailAddress'] && (
                  <Subtitle3 className={css.errorMessage}>
                    {formikProps.errors['targetEmailAddress']}
                  </Subtitle3>
                )}
              </div>
              <div className={css.row}>
                <div className={css.buttonContainer}>
                  <Button
                    type="primary"
                    className={css.button}
                    isLoading={formikProps.isSubmitting}
                    onClick={formikProps.handleSubmit}
                  >
                    Submit
                  </Button>
                </div>

                {submitError && (
                  <Subtitle3
                    className={classNames(css.errorMessage, css.submitError)}
                  >
                    {submitError}
                  </Subtitle3>
                )}
              </div>
            </>
          )
        }}
      </Formik>
    </div>
  )
}
